import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './api';
import type {
  EmailIntegrationStatus,
  EmailIntegrationAuthUrlResponse,
  EmailIntegrationTestResponse,
  SmtpConnectRequest,
  SmtpVerifyResponse,
} from '@clareefai/types';

export const teamMemberEmailIntegrationApi = createApi({
  reducerPath: 'teamMemberEmailIntegrationApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['TeamMemberEmailIntegration'],
  endpoints: (builder) => ({
    getTeamMemberEmailIntegrationStatus: builder.query<EmailIntegrationStatus, void>({
      query: () => '/team-members/me/email-integration/status',
      providesTags: ['TeamMemberEmailIntegration'],
    }),
    getTeamMemberGoogleAuthUrl: builder.query<EmailIntegrationAuthUrlResponse, void>({
      query: () => '/team-members/me/email-integration/google/auth-url',
    }),
    connectTeamMemberGoogleEmail: builder.mutation<EmailIntegrationStatus, { code: string }>({
      query: (body) => ({
        url: '/team-members/me/email-integration/google/callback',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TeamMemberEmailIntegration'],
    }),
    getTeamMemberMicrosoftAuthUrl: builder.query<EmailIntegrationAuthUrlResponse, void>({
      query: () => '/team-members/me/email-integration/microsoft/auth-url',
    }),
    connectTeamMemberMicrosoftEmail: builder.mutation<EmailIntegrationStatus, { code: string }>({
      query: (body) => ({
        url: '/team-members/me/email-integration/microsoft/callback',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TeamMemberEmailIntegration'],
    }),
    updateTeamMemberEmailDelegationCategories: builder.mutation<
      EmailIntegrationStatus,
      { enabledCategories: string[] }
    >({
      query: (body) => ({
        url: '/team-members/me/email-integration/categories',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['TeamMemberEmailIntegration'],
    }),
    sendTeamMemberTestEmail: builder.mutation<EmailIntegrationTestResponse, void>({
      query: () => ({
        url: '/team-members/me/email-integration/test',
        method: 'POST',
      }),
    }),
    disconnectTeamMemberEmailIntegration: builder.mutation<void, void>({
      query: () => ({
        url: '/team-members/me/email-integration/disconnect',
        method: 'DELETE',
      }),
      invalidatesTags: ['TeamMemberEmailIntegration'],
    }),
    verifyTeamMemberSmtp: builder.mutation<SmtpVerifyResponse, SmtpConnectRequest>({
      query: (body) => ({
        url: '/team-members/me/email-integration/smtp/verify',
        method: 'POST',
        body,
      }),
    }),
    connectTeamMemberSmtp: builder.mutation<EmailIntegrationStatus, SmtpConnectRequest>({
      query: (body) => ({
        url: '/team-members/me/email-integration/smtp/connect',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TeamMemberEmailIntegration'],
    }),
  }),
});

export const {
  useGetTeamMemberEmailIntegrationStatusQuery,
  useGetTeamMemberGoogleAuthUrlQuery,
  useConnectTeamMemberGoogleEmailMutation,
  useGetTeamMemberMicrosoftAuthUrlQuery,
  useConnectTeamMemberMicrosoftEmailMutation,
  useUpdateTeamMemberEmailDelegationCategoriesMutation,
  useSendTeamMemberTestEmailMutation,
  useDisconnectTeamMemberEmailIntegrationMutation,
  useVerifyTeamMemberSmtpMutation,
  useConnectTeamMemberSmtpMutation,
} = teamMemberEmailIntegrationApi;
